import { useMemo } from "react";
import { SearchableSelectOption } from "../../../Components/Common/SearchableSelect";
import { useCommunesQuery, usePrioritesQuery, useStatutAlertesQuery } from "../../../hooks/queries/useRoadAlertQueries";

interface FilterOptions {
  communeOptions: SearchableSelectOption[];
  prioriteOptions: SearchableSelectOption[];
  statutOptions: SearchableSelectOption[];
}

/**
 * Hook pour les données de filtres (communes, priorités, statuts) via TanStack Query
 */
export const useFilterData = () => {
  const communesQuery = useCommunesQuery();
  const prioritesQuery = usePrioritesQuery();
  const statutsQuery = useStatutAlertesQuery();

  const communes = useMemo(() => {
    const d = communesQuery.data;
    return Array.isArray(d) ? d : [];
  }, [communesQuery.data]);
  const priorites = useMemo(() => {
    const d = prioritesQuery.data;
    return Array.isArray(d) ? d : [];
  }, [prioritesQuery.data]);
  const statuts = useMemo(() => {
    const d = statutsQuery.data;
    return Array.isArray(d) ? d : [];
  }, [statutsQuery.data]);
  const loading = communesQuery.isLoading || prioritesQuery.isLoading || statutsQuery.isLoading;
  const error = communesQuery.error ?? prioritesQuery.error ?? statutsQuery.error;

  const options: FilterOptions = useMemo(() => {
    const communesMap = new Map<string, SearchableSelectOption>();
    communesMap.set("", { value: "", label: "--Tous--" });
    (Array.isArray(communes) ? communes : []).forEach((c: any) => {
      const value = String(c?.idCommune ?? c?.id ?? "");
      const label = c?.libelleCommune ?? c?.libelle ?? "Commune sans nom";
      if (!communesMap.has(value)) {
        communesMap.set(value, { value, label });
      }
    });

    const prioritesMap = new Map<string, SearchableSelectOption>();
    prioritesMap.set("", { value: "", label: "--Tous--" });
    (Array.isArray(priorites) ? priorites : []).forEach((p: any) => {
      const value = String(p?.id ?? p?.idPriorite ?? p?.value ?? "");
      const label = p?.libellePriorite ?? p?.libelle ?? p?.label ?? `Priorité ${value}`;
      if (value !== "" && !prioritesMap.has(value)) {
        prioritesMap.set(value, { value, label });
      }
    });

    const statutsMap = new Map<string, SearchableSelectOption>();
    statutsMap.set("", { value: "", label: "--Tous--" });
    (Array.isArray(statuts) ? statuts : []).forEach((s: any) => {
      const value = String(s?.id ?? s?.idStatutAlerte ?? s?.value ?? "");
      const label = s?.libelleStatutAlerte ?? s?.libelle ?? s?.label ?? `Statut ${value}`;
      if (value !== "" && !statutsMap.has(value)) {
        statutsMap.set(value, { value, label });
      }
    });

    return {
      communeOptions: Array.from(communesMap.values()),
      prioriteOptions: Array.from(prioritesMap.values()),
      statutOptions: Array.from(statutsMap.values()),
    };
  }, [communes, priorites, statuts]);

  return {
    communes,
    priorites,
    statuts,
    options,
    loading,
    error: error ? String(error) : null,
  };
};
